/**
 * Animation Store
 * Manages animation state and integrates with Flowi's store system
 */

import { create } from 'zustand';
import {
  AnimationType,
  AnimationPreset
} from 'src/features/animations/animationTypes';

interface AnimationState {
  // Active animations by target ID
  activeAnimations: Map<string, string[]>; // targetId -> animationIds[]

  // Animation presets
  presets: AnimationPreset[];

  // Global animation settings
  settings: {
    enabled: boolean;
    speed: number; // Global speed multiplier
    quality: 'low' | 'medium' | 'high';
  };

  // Selected animations in UI
  selectedAnimations: {
    type: AnimationType;
    targetIds: string[];
  } | null;

  // When set, animated renderers use this exact time instead of live time.
  // GIF export advances it frame by frame and clears it during cleanup.
  exportTimeMs: number | null;
}

interface AnimationActions {
  // Animation management
  addAnimation: (targetId: string, animationId: string) => void;
  removeAnimation: (targetId: string, animationId: string) => void;
  clearAnimations: (targetId: string) => void;
  clearAllAnimations: () => void;

  // Preset management
  addPreset: (preset: AnimationPreset) => void;
  removePreset: (name: string) => void;
  updatePreset: (name: string, preset: Partial<AnimationPreset>) => void;

  // Settings
  updateSettings: (settings: Partial<AnimationState['settings']>) => void;
  toggleAnimations: () => void;
  setAnimationSpeed: (speed: number) => void;
  setExportTime: (timeMs: number | null) => void;

  // Selection
  selectAnimation: (type: AnimationType, targetIds: string[]) => void;
  clearSelection: () => void;

  // Queries
  getAnimationsForTarget: (targetId: string) => string[];
  hasActiveAnimations: (targetId: string) => boolean;
  getPresetByName: (name: string) => AnimationPreset | undefined;
}

export type AnimationStore = AnimationState & {
  actions: AnimationActions;
};

/**
 * Create the animation store
 */
export const useAnimationStore = create<AnimationStore>((set, get) => ({
  // Initial state
  activeAnimations: new Map(),
  presets: [
    // Default presets
    {
      name: 'Gentle Pulse',
      description: 'Subtle breathing effect',
      type: 'pulse',
      config: {
        duration: 2000,
        easing: 'easeInOutSine',
        repeat: -1
      }
    },
    {
      name: 'Data Flow',
      description: 'Flowing data particles',
      type: 'dataFlow',
      config: {
        duration: 3000,
        repeat: -1
      }
    },
    {
      name: 'Attention',
      description: 'Grab user attention',
      type: 'pulse',
      config: {
        duration: 500,
        repeat: 3,
        easing: 'easeOutElastic'
      }
    }
  ],
  settings: {
    enabled: true,
    speed: 1,
    quality: 'medium'
  },
  selectedAnimations: null,
  exportTimeMs: null,

  // Actions
  actions: {
    addAnimation: (targetId: string, animationId: string) => {
      set((state) => {
        const newAnimations = new Map(state.activeAnimations);
        const targetAnimations = newAnimations.get(targetId) || [];
        if (!targetAnimations.includes(animationId)) {
          newAnimations.set(targetId, [...targetAnimations, animationId]);
        }
        return { activeAnimations: newAnimations };
      });
    },

    removeAnimation: (targetId: string, animationId: string) => {
      set((state) => {
        const newAnimations = new Map(state.activeAnimations);
        const targetAnimations = newAnimations.get(targetId) || [];
        const filtered = targetAnimations.filter((id) => id !== animationId);
        if (filtered.length === 0) {
          newAnimations.delete(targetId);
        } else {
          newAnimations.set(targetId, filtered);
        }
        return { activeAnimations: newAnimations };
      });
    },

    clearAnimations: (targetId: string) => {
      set((state) => {
        const newAnimations = new Map(state.activeAnimations);
        newAnimations.delete(targetId);
        return { activeAnimations: newAnimations };
      });
    },

    clearAllAnimations: () => {
      set({ activeAnimations: new Map() });
    },

    addPreset: (preset: AnimationPreset) => {
      set((state) => ({
        presets: [...state.presets, preset]
      }));
    },

    removePreset: (name: string) => {
      set((state) => ({
        presets: state.presets.filter((p) => p.name !== name)
      }));
    },

    updatePreset: (name: string, updates: Partial<AnimationPreset>) => {
      set((state) => ({
        presets: state.presets.map((p) =>
          p.name === name ? { ...p, ...updates } : p
        )
      }));
    },

    updateSettings: (settings: Partial<AnimationState['settings']>) => {
      set((state) => ({
        settings: { ...state.settings, ...settings }
      }));
    },

    toggleAnimations: () => {
      set((state) => ({
        settings: { ...state.settings, enabled: !state.settings.enabled }
      }));
    },

    setAnimationSpeed: (speed: number) => {
      set((state) => ({
        settings: { ...state.settings, speed }
      }));
    },

    setExportTime: (timeMs: number | null) => {
      set({ exportTimeMs: timeMs });
    },

    selectAnimation: (type: AnimationType, targetIds: string[]) => {
      set({ selectedAnimations: { type, targetIds } });
    },

    clearSelection: () => {
      set({ selectedAnimations: null });
    },

    getAnimationsForTarget: (targetId: string) => {
      return get().activeAnimations.get(targetId) || [];
    },

    hasActiveAnimations: (targetId: string) => {
      const animations = get().activeAnimations.get(targetId);
      return animations ? animations.length > 0 : false;
    },

    getPresetByName: (name: string) => {
      return get().presets.find((p) => p.name === name);
    }
  }
}));

// Selector hooks for common queries
export const useActiveAnimations = (targetId: string) => {
  return useAnimationStore((state) =>
    state.actions.getAnimationsForTarget(targetId)
  );
};

export const useAnimationSettings = () => {
  return useAnimationStore((state) => state.settings);
};

export const useAnimationPresets = () => {
  return useAnimationStore((state) => state.presets);
};
